import './lib/webgpuLimits.js'  // side-effect: patch GPUAdapter.requestDevice BEFORE any ORT webgpu device (keep first)
import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'

import App from './App.jsx'
import ErrorBoundary from './components/ErrorBoundary.jsx'
import DebugEnvApp from './components/DebugEnvApp.jsx'
import EyeTestApp from './components/EyeTestApp.jsx'
import { ProtectedRoute } from './components/auth/ProtectedRoute.jsx'
import { AuthProvider } from './lib/auth/AuthContext.jsx'
import { applyBootHint } from './lib/palettes/paletteStore.js'
import './styles/tokens.css'
import './styles/index.css'
import './styles/studio.css'  // Hanken Grotesk @font-face + body → var(--m-font); after index.css so its body rule wins
import './styles/graphEditor.css' // graph-editor selection ring + click-through group overlay (ENG-1068)
import './lib/assetUrl.js'   // side-effect: rewrites /assets/* if VITE_ASSETS_BASE_URL is set
import './lib/staticAssetCache.js' // side-effect: Cache-Storage window.fetch wrapper for the big bundled ORT wasm + rig2mesh model
import './lib/perfProbe.js'  // side-effect: exposes window.__PERF__
import './lib/bodyMotionNav.js' // side-effect: exposes window.__GEN_BODY_MOTION__ / __GEN_BODY_MOTION_ON_NAVMESH__
import './lib/generatedGestureClip.js' // side-effect: exposes window.__PLAY_GENERATED_GESTURE__

// Suppress noisy React Flow handle warnings that flood the console
const _warn = console.warn.bind(console)
console.warn = (...args) => {
  if (typeof args[0] === 'string' && args[0].startsWith('[React Flow]')) return
  _warn(...args)
}

// NOTE: the ?editor_popup=1 AND ?material_popup=<id> standalone editor shells
// are both GONE. Every editor pop-out is now a PORTAL of the main window's own
// instance (FloatingWindow popped mode — see App.jsx useWindowPopOut), so there
// is no second boot, no divergent module-singleton state, and no window.open to
// be silently refused or silently reused. The material graph was the last
// holdout; legacy URLs carrying either param simply boot the normal app.

// Apply the last-used palette synchronously (pre-auth) so a non-default user
// doesn't flash the default theme; initPalette() reconciles per-user + server
// after auth (App mount). See paletteStore.js.
applyBootHint()

const params = new URLSearchParams(window.location.search)
// ?debug_env=1 — env-lighting debug harness; NO gaussian characters loaded.
const isDebugEnv = params.get('debug_env') === '1'
// ?eyetest=1 — the eye test scene. Behind ProtectedRoute like every other route: it serves the
// character's trained ONNX + statics, so an unauthenticated visitor must not reach it. It still
// mounts NO character, blueprint or head pipeline, which is what keeps a failure there about
// the eyes.
const isEyeTest = params.get('eyetest') === '1'

function Root() {
  if (isDebugEnv) return <DebugEnvApp />
  return <App />
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <Routes>
            <Route element={<ProtectedRoute />}>
              <Route path="/*" element={
                isEyeTest ? <EyeTestApp />
                : <App />
              } />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  </React.StrictMode>
)
